'use client';

import React, { useState, useEffect } from 'react';
import { ControlMode } from './RunSceneShell';
import AppLogo from '@/components/ui/AppLogo';

interface PermissionGateProps {
  onGranted: (mode: ControlMode) => void;
}

type GateState = 'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported';

function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const result = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  console.log('[PermissionGate] isMobileDevice() →', result, '| UA:', navigator.userAgent.slice(0, 80));
  return result;
}

export default function PermissionGate({ onGranted }: PermissionGateProps) {
  const [gateState, setGateState] = useState<GateState>('idle');
  const [isMobile, setIsMobile] = useState(false);
  const [desktopGranted, setDesktopGranted] = useState(false);

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    // Desktop: skip permission gate entirely — keyboard controls work immediately
    if (!mobile) {
      console.log('[PermissionGate] ✅ Desktop detected — bypassing motion gate, granting touch mode immediately');
      setDesktopGranted(true);
      onGranted('touch');
    } else {
      console.log('[PermissionGate] 📱 Mobile detected — showing motion permission gate');
    }
  }, [onGranted]);

  const requestMotionPermission = async () => {
    setGateState('requesting');
    console.log('[PermissionGate] Requesting DeviceOrientation permission…');

    const DevOrientEvent = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };

    if (typeof DevOrientEvent.requestPermission === 'function') {
      console.log('[PermissionGate] iOS-style requestPermission() API detected');
      try {
        const permission = await DevOrientEvent.requestPermission();
        console.log('[PermissionGate] DeviceOrientationEvent.requestPermission() result:', permission);
        if (permission === 'granted') {
          setGateState('granted');
          console.log('[PermissionGate] ✅ Motion permission granted — launching tilt mode in 600ms');
          setTimeout(() => onGranted('tilt'), 600);
        } else {
          console.warn('[PermissionGate] ❌ Motion permission denied by user');
          setGateState('denied');
        }
      } catch (err) {
        console.error('[PermissionGate] ❌ requestPermission() threw:', err);
        setGateState('denied');
      }
    } else if ('DeviceOrientationEvent' in window) {
      // Android / non-permission browsers — orientation available without prompt
      console.log('[PermissionGate] ✅ DeviceOrientationEvent available without prompt (Android/non-iOS) — granting tilt mode');
      setGateState('granted');
      setTimeout(() => onGranted('tilt'), 600);
    } else {
      console.warn('[PermissionGate] ⚠️ DeviceOrientationEvent not supported in this browser');
      setGateState('unsupported');
    }
  };

  // On mobile, motion is REQUIRED — no skip option
  // On desktop this component never renders (auto-granted above)
  if (!isMobile) {
    // Desktop: render nothing — onGranted already called in useEffect
    if (desktopGranted) return null;
    // Brief initial render before useEffect fires — show nothing
    return null;
  }

  return (
    <div className="absolute inset-0 z-50 bg-[#0d0d0f] flex flex-col items-center justify-between px-6 py-10 fade-in-fast">
      {/* Background gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(0,229,255,0.07) 0%, transparent 70%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(255,77,109,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Logo */}
      <div className="relative z-10 flex flex-col items-center gap-3 mt-4">
        <div className="flex items-center gap-3">
          <AppLogo size={44} />
          <span
            className="text-2xl font-bold tracking-tight"
            style={{ fontFamily: 'DM Sans, sans-serif', color: '#00e5ff' }}
          >
            Surfsterre
          </span>
        </div>
        <p className="text-[#8899aa] text-xs tracking-widest uppercase font-medium">
          Skate the World
        </p>
      </div>

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-xs">
        {/* Rider silhouette */}
        <div
          className="w-40 h-40 rounded-full flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle, rgba(0,229,255,0.12) 0%, rgba(0,229,255,0.03) 70%)',
            border: '2px solid rgba(0,229,255,0.25)',
          }}
        >
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
            <circle cx="40" cy="18" r="10" fill="rgba(0,229,255,0.6)" />
            <rect x="32" y="30" width="16" height="22" rx="4" fill="rgba(0,229,255,0.5)" />
            <path d="M50 20 Q62 14 58 28" stroke="rgba(255,214,10,0.8)" strokeWidth="3" strokeLinecap="round" fill="none" />
            <rect x="18" y="32" width="14" height="6" rx="3" fill="rgba(0,229,255,0.4)" />
            <rect x="48" y="32" width="14" height="6" rx="3" fill="rgba(0,229,255,0.4)" />
            <rect x="32" y="52" width="8" height="18" rx="3" fill="rgba(0,229,255,0.4)" />
            <rect x="42" y="52" width="8" height="18" rx="3" fill="rgba(0,229,255,0.4)" />
            <rect x="22" y="70" width="36" height="6" rx="3" fill="rgba(255,77,109,0.7)" />
            <circle cx="29" cy="76" r="3" fill="rgba(0,229,255,0.8)" />
            <circle cx="51" cy="76" r="3" fill="rgba(0,229,255,0.8)" />
          </svg>
        </div>

        {gateState === 'idle' && (
          <div className="flex flex-col items-center gap-5 w-full slide-up-in">
            <div className="text-center">
              <h2
                className="text-xl font-bold text-white mb-2"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                📱 Motion Required
              </h2>
              <p className="text-[#8899aa] text-sm leading-relaxed">
                Surfsterre uses your phone&apos;s motion sensor to steer left and right. Motion access is required to play.
              </p>
            </div>

            <button
              onClick={requestMotionPermission}
              className="permission-pulse w-full py-4 rounded-2xl font-bold text-base tracking-wide btn-action"
              style={{
                background: 'linear-gradient(135deg, #00e5ff 0%, #0077ff 100%)',
                color: '#0d0d0f',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Enable Motion & Play
            </button>

            <p className="text-[#556677] text-xs text-center">
              Tilt your phone left/right to steer. Motion data stays on your device.
            </p>
          </div>
        )}

        {gateState === 'requesting' && (
          <div className="flex flex-col items-center gap-4 slide-up-in">
            <div className="w-10 h-10 border-3 border-[#00e5ff] border-t-transparent rounded-full animate-spin" />
            <p className="text-[#00e5ff] text-sm font-medium">Requesting permission…</p>
          </div>
        )}

        {gateState === 'granted' && (
          <div className="flex flex-col items-center gap-4 slide-up-in">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,229,255,0.15)', border: '2px solid #00e5ff' }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00e5ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className="neon-cyan font-bold text-lg">Motion Enabled!</p>
            <p className="text-[#8899aa] text-sm">Loading your run…</p>
          </div>
        )}

        {gateState === 'denied' && (
          <div className="flex flex-col items-center gap-5 w-full slide-up-in">
            <div className="text-center">
              <p className="text-[#ff4d6d] font-bold text-lg mb-1">⚠️ Motion Denied</p>
              <p className="text-[#8899aa] text-sm leading-relaxed">
                Motion access is required to steer. Please allow motion in your browser settings and try again.
              </p>
            </div>
            <button
              onClick={requestMotionPermission}
              className="w-full py-4 rounded-2xl font-bold text-base tracking-wide btn-action"
              style={{
                background: 'linear-gradient(135deg, #ff4d6d 0%, #c9184a 100%)',
                color: '#fff',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Try Again
            </button>
            <p className="text-[#556677] text-xs text-center">
              Go to Settings → Safari/Chrome → Motion & Orientation Access → Allow
            </p>
          </div>
        )}

        {gateState === 'unsupported' && (
          <div className="flex flex-col items-center gap-5 w-full slide-up-in">
            <div className="text-center">
              <p className="text-[#ffd60a] font-bold text-lg mb-1">Motion Not Available</p>
              <p className="text-[#8899aa] text-sm leading-relaxed">
                Your browser doesn&apos;t support motion sensors. Try opening this in Safari or Chrome.
              </p>
            </div>
            <button
              onClick={() => onGranted('touch')}
              className="w-full py-4 rounded-2xl font-bold text-base tracking-wide btn-action"
              style={{
                background: 'linear-gradient(135deg, #ffd60a 0%, #f4a261 100%)',
                color: '#0d0d0f',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Play Anyway (Touch)
            </button>
          </div>
        )}
      </div>

      {/* Bottom hint */}
      <div className="relative z-10 flex flex-col items-center gap-1">
        <p className="text-[#556677] text-xs text-center">
          Motion data stays on your device — never sent to any server
        </p>
        <p className="text-[#334455] text-xs">Surfsterre v1.0</p>
      </div>
    </div>
  );
}